import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../utils/api";
import { motion } from "framer-motion";
import { FaBalanceScale } from "react-icons/fa";

/* =========================
  Helpers
========================= */
const normalizePhone = (value) => String(value || "").replace(/\D/g, "");
const isValidEgyptPhone = (phone) => /^01[0125][0-9]{8}$/.test(String(phone || ""));

function Login({ setToken }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  // message: { type: "success" | "error" | "warning", text: string, code?: string, meta?: any }
  const [message, setMessage] = useState(null);

  const navigate = useNavigate();
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const isBlockedCode = (code) =>
    code === "SUBSCRIPTION_EXPIRED" || code === "ACCOUNT_FROZEN";

  const goToRenew = () => {
    const cleanPhone = normalizePhone(phone);

    if (!cleanPhone || !password) {
      setMessage({
        type: "warning",
        text: "⚠️ من فضلك اكتب رقم الموبايل وكلمة المرور ثم اضغط تجديد الاشتراك",
      });
      return;
    }

    if (!isValidEgyptPhone(cleanPhone)) {
      setMessage({
        type: "warning",
        text: "⚠️ رقم الموبايل غير صحيح (مثال: 01012345678)",
      });
      return;
    }

    try {
      const existing = localStorage.getItem("auth_block");

      if (!existing && message?.code && isBlockedCode(message.code)) {
localStorage.setItem(
  "auth_block",
  JSON.stringify({
    code,
    message: msg,
    phone: cleanPhone, // ✅ مهم جداً
    subscriptionStatus: data?.subscriptionStatus,
    subscriptionExpiresAt: data?.subscriptionExpiresAt,
    at: Date.now(),
  })
);
      }
    } catch (_) {}

    navigate("/subscribe", {
      state: {
        renewPhone: cleanPhone,
        renewPassword: password,
      },
    });
  };

  const handleLogin = async () => {
    const cleanPhone = normalizePhone(phone);

    if (!cleanPhone || !password) {
      setMessage({ type: "warning", text: "⚠️ من فضلك اكتب رقم الموبايل وكلمة المرور" });
      return;
    }

    if (!isValidEgyptPhone(cleanPhone)) {
      setMessage({ type: "warning", text: "⚠️ رقم الموبايل غير صحيح (مثال: 01012345678)" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      // ✅ login by phone
      const res = await api.post("/auth/login", { phone: cleanPhone, password });
      const data = res?.data || {};

      const role = data?.role || data?.user?.role || "";

      const userIdFromServer =
        data?.user?._id || data?.userId || data?.id || data?._id || "";

      if (!data?.token) {
        throw new Error("⚠️ لم يتم استلام token من السيرفر.");
      }

      if (!userIdFromServer) {
        throw new Error("⚠️ لم يتم استلام userId من السيرفر. راجع استجابة /auth/login.");
      }

      const oldUserId = localStorage.getItem("userId");
      if (oldUserId && String(oldUserId).startsWith("anon_")) {
        localStorage.removeItem("userId");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", role);
      localStorage.setItem("userId", userIdFromServer);

      // ✅ امسح أي بلوك قديم
      localStorage.removeItem("auth_block");

      setToken?.(data.token);

      setMessage({
        type: "success",
        text: data?.message || "✅ تم تسجيل الدخول بنجاح!",
      });

      const securityQuestionSet =
        data?.securityQuestionSet ?? data?.user?.securityQuestionSet;

      setTimeout(() => {
        if (securityQuestionSet === false) {
          navigate("/setup-security-question");
          return;
        }

        if (role === "Admin") navigate("/admin");
        else if (role === "محامي مساعد") navigate("/join");
        else navigate("/dashboard");
      }, 650);
    } catch (err) {
      console.error("❌ Login Error:", err);

      const status = err?.response?.status;
      const data = err?.response?.data || {};

      const code = data?.code;
      const msg =
        data?.message ||
        err?.message ||
        "⚠️ حصل خطأ أثناء تسجيل الدخول. تأكد من البيانات وجرب تاني.";

      // ✅ لو الحساب متجمد أو منتهي
      if (status === 403 && isBlockedCode(code)) {
        try {
          localStorage.setItem(
            "auth_block",
            JSON.stringify({
              code,
              message: msg,
              subscriptionStatus: data?.subscriptionStatus,
              subscriptionExpiresAt: data?.subscriptionExpiresAt,
              at: Date.now(),
            })
          );
        } catch (_) {}

        setMessage({
          type: "error",
          text: msg,
          code,
          meta: {
            subscriptionStatus: data?.subscriptionStatus,
            subscriptionExpiresAt: data?.subscriptionExpiresAt,
          },
        });

        return;
      }

      setMessage({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  const showRenewButton = !!(message?.type === "error" && isBlockedCode(message?.code));

  return (
    <div
      className="flex items-center justify-center h-screen w-full bg-cover bg-center relative px-4"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1600334129128-685c5a7b4f01?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')",
      }}
    >
      <div className="absolute inset-0 bg-black/80" />

      <motion.div
        initial={{ opacity: 0, y: -35 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 bg-gray-900/95 border border-yellow-500 p-8 md:p-10 rounded-2xl shadow-2xl w-full max-w-lg text-center"
      >
        <FaBalanceScale className="text-yellow-400 text-5xl mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-yellow-400 mb-2">تسجيل الدخول</h2>
        <p className="text-sm text-gray-400 mb-6">
          سجل دخولك برقم الموبايل عشان تفتح لوحة التحكم ⚖️
        </p>

        <div className="text-start">
          <label className="block text-xs text-gray-400 mb-1">📱 رقم الموبايل</label>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="مثال: 01012345678"
            className="w-full p-3 mb-2 bg-gray-800 rounded border border-gray-700 text-white focus:outline-none focus:border-yellow-400 placeholder-gray-400"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={onEnter}
          />

          {phone && (
            <p className="text-[11px] text-gray-500 mb-3">
              {isValidEgyptPhone(normalizedPhone)
                ? `✅ الرقم مضبوط: ${normalizedPhone}`
                : `ℹ️ الرقم بعد التنظيف: ${normalizedPhone || "—"}`}
            </p>
          )}

          <label className="block text-xs text-gray-400 mb-1">🔒 كلمة المرور</label>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="اكتب كلمة المرور"
            className="w-full p-3 mb-4 bg-gray-800 rounded border border-gray-700 text-white focus:outline-none focus:border-yellow-400 placeholder-gray-400"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onEnter}
          />

          <div className="text-right mb-4">
            <Link to="/forgot-password" className="text-xs text-yellow-400 hover:underline">
              هل نسيت كلمة السر؟
            </Link>
          </div>
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          className={`w-full py-3 font-bold rounded-lg shadow-md transition-all duration-300 ${
            loading
              ? "bg-gray-500 cursor-not-allowed text-gray-300"
              : "bg-yellow-500 hover:bg-yellow-400 text-black"
          }`}
        >
          {loading ? "⏳ جاري تسجيل الدخول..." : "🚀 دخول"}
        </button>

        <div className="flex items-center justify-between mt-5 text-sm">
          <p className="text-gray-400">
            ليس لديك حساب؟{" "}
            <Link to="/register" className="text-yellow-400 hover:underline">
              تسجيل جديد
            </Link>
          </p>

          <Link to="/" className="text-gray-400 hover:text-yellow-300 transition">
            الرجوع للرئيسية
          </Link>
        </div>

        <p className="mt-4 text-xs text-yellow-500">
          أهلاً بك في نظام <b>الأفوكاتو</b> ⚖️
        </p>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className={`mt-6 p-3 rounded-lg text-sm font-semibold ${
              message.type === "success"
                ? "bg-green-600/30 text-green-300 border border-green-500"
                : message.type === "error"
                ? "bg-red-600/30 text-red-300 border border-red-500"
                : "bg-yellow-600/30 text-yellow-300 border border-yellow-500"
            }`}
          >
            {message.text}

            {showRenewButton && (
              <div className="mt-3">
                <button
                  onClick={goToRenew}
                  className="w-full py-2 rounded-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition"
                >
                  🔁 تجديد الاشتراك
                </button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

export default Login;
